// Says, next to a button, who at the firm gets a bell notification when it's pressed, or that nobody does.
// Where staff can choose, it's a Notify checkbox instead (NotifyToggle, for a save), or one per person (NotifyPicker,
// for a comment or a shared note).
// The API decides who hears what (apps/api/src/modules/feedback/notifications.ts); these helpers mirror it.
import { useId, type ReactNode } from 'react';
import { Bell, BellOff } from 'lucide-react';
import { cx } from '../../ui';
import type { RequestDetail } from './types';

/** "Ann", "Ann and Bo", "Ann, Bo and Cy", "Ann, Bo, Cy and 2 others". */
export function names(list: string[]): string {
  const shown = list.length > 4 ? [...list.slice(0, 3), `${list.length - 3} others`] : list;
  return shown.length <= 1 ? (shown[0] ?? '') : `${shown.slice(0, -1).join(', ')} and ${shown.at(-1)}`;
}

/**
 * Who hears about a change on a request: `requester` (a question), `thread` (the requester and their notify
 * list: comments, estimates, other updates) or `resolved` (the thread, plus the whole firm when they asked).
 */
export function audience(r: Pick<RequestDetail, 'submitted_by_name' | 'watchers' | 'notify_whole_firm'>, scope: 'requester' | 'thread' | 'resolved'): string[] {
  if (scope === 'resolved' && r.notify_whole_firm) return ['everyone at the firm'];
  return scope === 'requester' ? [r.submitted_by_name] : [r.submitted_by_name, ...r.watchers.map((w) => w.name)];
}

/** `to` null: nobody at the firm is notified. */
export function NotifyNote({ to, what, className }: { to: string[] | null; what?: ReactNode; className?: string }) {
  const Icon = to ? Bell : BellOff;
  return (
    <p className={cx('m-0 flex items-start gap-1.5 text-[13px]', to ? 'text-ink' : 'text-muted', className)}>
      <Icon aria-hidden size={14} className={cx('mt-0.5 flex-none', to && 'text-brand')} />
      <span>
        {to ? <><span className="font-semibold">Notifies {names(to)}</span>{what && <>: {what}</>}</> : <><span className="font-semibold">Nobody at the firm is notified</span>{what && <>. {what}</>}</>}
      </span>
    </p>
  );
}

export type NotifyPerson = { id: string; name: string; requester?: boolean };

/** The people a staff comment can notify: the requester, then their notify list. */
export function threadPeople(r: Pick<RequestDetail, 'submitted_by' | 'submitted_by_name' | 'watchers'>): NotifyPerson[] {
  return [{ id: r.submitted_by, name: r.submitted_by_name, requester: true }, ...r.watchers.filter((w) => w.id !== r.submitted_by)];
}

/** One Notify checkbox per person, for a comment. Nobody ticked: it still shows on their request, with no bell. */
export function NotifyPicker({ people, what, chosen, onChange }: { people: NotifyPerson[]; what?: ReactNode; chosen: string[]; onChange: (ids: string[]) => void }) {
  const hint = useId();
  const none = chosen.length === 0;
  const toggle = (id: string, on: boolean) => onChange(people.map((p) => p.id).filter((x) => (x === id ? on : chosen.includes(x))));
  return (
    <fieldset className="m-0 flex flex-col gap-1 border-0 p-0" aria-describedby={none ? hint : undefined}>
      <legend className="mb-1 p-0 text-[13px] text-ink"><span className="font-semibold">Notify</span>{what && <>: {what}</>}</legend>
      {people.map((p) => (
        <label key={p.id} className="flex cursor-pointer items-start gap-2 text-[13px] text-ink">
          <input type="checkbox" checked={chosen.includes(p.id)} onChange={(e) => toggle(p.id, e.target.checked)} className="mt-0.5 h-4 w-4 flex-none accent-brand" />
          <span>{p.name}{p.requester && <span className="text-muted"> (requester)</span>}</span>
        </label>
      ))}
      {people.length === 1 && <span className="pl-6 text-xs text-muted">Nobody else is on their notify list.</span>}
      {none && <span id={hint} className="pl-6 text-xs text-muted">No notification. It still shows on their request.</span>}
    </fieldset>
  );
}

/** A Notify checkbox naming who would hear. Unticked, the change still shows on their request, with no bell. */
export function NotifyToggle({ to, what, checked, onChange }: { to: string[]; what?: ReactNode; checked: boolean; onChange: (on: boolean) => void }) {
  const hint = useId();
  return (
    <div className="flex flex-col gap-0.5">
      <label className="flex cursor-pointer items-start gap-2 text-[13px] text-ink">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} aria-describedby={checked ? undefined : hint}
          className="mt-0.5 h-4 w-4 flex-none accent-brand" />
        <span><span className="font-semibold">Notify {names(to)}</span>{what && <>: {what}</>}</span>
      </label>
      {!checked && <span id={hint} className="pl-6 text-xs text-muted">No notification. It still shows on their request.</span>}
    </div>
  );
}
